import { Card, Title, Text } from '@tremor/react';
import { LoadingSkeleton } from './LoadingSkeleton';

export interface CohortBucket {
  cohort_week: string;
  size: number;
  retention: number[]; // 0..1 per offset week
}

interface Props {
  cohorts: CohortBucket[];
  loading?: boolean;
  error?: string | null;
  title?: string;
  description?: string;
  metric?: string;
}

function colorForRetention(v: number): string {
  // Map 0..1 to a blue scale. We use a discrete bucket scheme so the matrix
  // reads like a heatmap without dragging in chroma.js.
  if (v >= 0.9) return 'bg-blue-500/95 text-white';
  if (v >= 0.7) return 'bg-blue-500/75 text-white';
  if (v >= 0.5) return 'bg-blue-500/55 text-blue-50';
  if (v >= 0.3) return 'bg-blue-500/35 text-blue-100';
  if (v >= 0.1) return 'bg-blue-500/20 text-blue-200';
  if (v > 0) return 'bg-blue-500/10 text-blue-300';
  return 'bg-slate-800/60 text-slate-500';
}

export function CohortMatrix({
  cohorts,
  loading,
  error,
  title = 'Retenção por cohort',
  description,
  metric,
}: Props) {
  if (loading) {
    return (
      <Card>
        <Title>{title}</Title>
        {description && <Text>{description}</Text>}
        <div className="mt-3">
          <LoadingSkeleton rows={6} />
        </div>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <Title>{title}</Title>
        <div className="mt-2 rounded border border-red-900/40 bg-red-950/20 p-3 text-sm text-red-300">
          {error}
        </div>
      </Card>
    );
  }
  if (!cohorts || cohorts.length === 0) {
    return (
      <Card>
        <Title>{title}</Title>
        <Text>Sem cohorts no período.</Text>
      </Card>
    );
  }

  const cols = cohorts[0]?.retention.length ?? 0;

  return (
    <Card>
      <Title>{title}</Title>
      {description && <Text>{description}</Text>}
      {metric && (
        <Text className="text-xs uppercase tracking-wide text-slate-500">
          métrica: {metric}
        </Text>
      )}
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-1 text-xs">
          <thead>
            <tr>
              <th className="sticky left-0 bg-slate-900 px-2 py-1 text-left text-slate-300">
                Cohort
              </th>
              <th className="px-2 py-1 text-left text-slate-400">Tamanho</th>
              {Array.from({ length: cols }).map((_, i) => (
                <th key={i} className="px-1 py-1 text-center text-slate-400">
                  W{i}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cohorts.map((c) => (
              <tr key={c.cohort_week}>
                <td className="sticky left-0 bg-slate-900 px-2 py-1 font-mono text-slate-200">
                  {c.cohort_week}
                </td>
                <td className="px-2 py-1 text-slate-400">{c.size}</td>
                {c.retention.map((v, i) => (
                  <td
                    key={i}
                    className={`min-w-[28px] rounded px-1 py-1 text-center ${colorForRetention(
                      v,
                    )}`}
                    title={`${(v * 100).toFixed(1)}%`}
                  >
                    {v > 0 ? `${Math.round(v * 100)}` : '·'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
